// apps/web/src/components/dashboard/DashboardSectionHeader.tsx
//
// Briefing Desk section header: small uppercase label, quiet rule,
// optional right-aligned aux text. The label is a semantic <h2> so the
// owning <section aria-labelledby> becomes a named landmark. When
// `collapsible`, the heading wraps a disclosure button.

import type { ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

export interface DashboardSectionHeaderProps {
  headingId: string
  label: string
  aux?: ReactNode
  collapsible?: boolean
  open?: boolean
  onToggle?: () => void
  /** id of the collapsible content region, wired to aria-controls. */
  contentId?: string
}

const LABEL_CLASSES =
  'text-[11px] font-medium uppercase tracking-[0.15em] text-muted-foreground'

export function DashboardSectionHeader(props: DashboardSectionHeaderProps) {
  return (
    <div className="flex items-baseline gap-3">
      {props.collapsible ? (
        <h2 id={props.headingId} className={LABEL_CLASSES}>
          <button
            type="button"
            aria-expanded={props.open}
            aria-controls={props.contentId}
            onClick={props.onToggle}
            className="inline-flex items-center gap-1.5 rounded-sm uppercase tracking-[0.15em] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {props.open ? (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            )}
            {props.label}
          </button>
        </h2>
      ) : (
        <h2 id={props.headingId} className={LABEL_CLASSES}>
          {props.label}
        </h2>
      )}
      <span aria-hidden="true" className="h-px flex-1 self-center bg-border" />
      {props.aux ? (
        <span className="text-xs text-muted-foreground">{props.aux}</span>
      ) : null}
    </div>
  )
}
